import { memo } from 'react'

import { ASPECT, VIEW_W, pctToView } from '@/geo/types'
import type { ToolState } from '@/features/annotator/tools/types'

/**
 * Whatever the active gesture is drawing right now: the in-progress polygon
 * with its rubber-band segment, the marquee, the band, the rectangle, the
 * portal link and the anchor's stand-point drag (§3.2 `ToolPreview.tsx`).
 *
 * Nothing here is interactive — it is a picture of the state machine, drawn
 * from `ToolState` alone, which is why it stays correct without any extra
 * bookkeeping.
 */

export interface ToolPreviewProps {
  state: ToolState
  colour: string
  /** Handle radius in percent of width, divided by the zoom. */
  radius: number
  /** True when the ring would be self-intersecting if closed now. */
  invalid?: boolean
}

export const ToolPreview = memo(function ToolPreview({ state, colour, radius, invalid }: ToolPreviewProps) {
  const stroke = invalid ? 'var(--color-danger)' : colour
  const r = Math.max(0.3, radius)

  switch (state.t) {
    case 'polygon.drawing':
    case 'path.drawing': {
      const pts = state.points.map((p) => pctToView(p))
      const cursor = pctToView(state.cursor)
      const first = pts[0]
      const d = first ? `M${first[0]},${first[1]}` + pts.slice(1).map((p) => `L${p[0]},${p[1]}`).join('') : ''
      return (
        <g pointerEvents="none">
          {d ? <path d={d} fill="none" stroke={stroke} strokeWidth={0.35} strokeLinejoin="round" vectorEffect="non-scaling-stroke" /> : null}
          {pts.length ? (
            <line
              x1={pts[pts.length - 1]![0]}
              y1={pts[pts.length - 1]![1]}
              x2={cursor[0]}
              y2={cursor[1]}
              stroke={stroke}
              strokeWidth={0.28}
              strokeDasharray="1 0.8"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {pts.map((p, i) => (
            <circle key={i} cx={p[0]} cy={p[1]} r={i === 0 ? r * 1.3 : r} fill={i === 0 ? stroke : 'white'} stroke={stroke} strokeWidth={r * 0.3} />
          ))}
        </g>
      )
    }

    case 'band.dragging': {
      const y0 = Math.min(state.y0, state.y1) * ASPECT
      const y1 = Math.max(state.y0, state.y1) * ASPECT
      return (
        <g pointerEvents="none">
          <rect x={0} y={y0} width={VIEW_W} height={y1 - y0} fill={colour} fillOpacity={0.24} stroke={colour} strokeWidth={0.3} vectorEffect="non-scaling-stroke" />
        </g>
      )
    }

    case 'rect.dragging': {
      const a = pctToView(state.from)
      const b = pctToView(state.to)
      return (
        <rect
          pointerEvents="none"
          x={Math.min(a[0], b[0])}
          y={Math.min(a[1], b[1])}
          width={Math.abs(b[0] - a[0])}
          height={Math.abs(b[1] - a[1])}
          fill={colour}
          fillOpacity={0.24}
          stroke={colour}
          strokeWidth={0.3}
          vectorEffect="non-scaling-stroke"
        />
      )
    }

    case 'marquee': {
      const a = pctToView(state.from)
      const b = pctToView(state.to)
      return (
        <rect
          pointerEvents="none"
          x={Math.min(a[0], b[0])}
          y={Math.min(a[1], b[1])}
          width={Math.abs(b[0] - a[0])}
          height={Math.abs(b[1] - a[1])}
          fill="var(--color-accent)"
          fillOpacity={0.14}
          stroke="var(--color-accent)"
          strokeWidth={0.25}
          strokeDasharray="1 0.7"
          vectorEffect="non-scaling-stroke"
        />
      )
    }

    case 'portal.linking': {
      const c = pctToView(state.cursor)
      return (
        <g pointerEvents="none">
          <circle cx={c[0]} cy={c[1]} r={r * 1.4} fill="none" stroke={colour} strokeWidth={r * 0.35} strokeDasharray="0.8 0.6" />
        </g>
      )
    }

    case 'anchor.placing': {
      if (!state.at) return null
      const a = pctToView(state.at)
      const s = state.stand ? pctToView(state.stand) : null
      return (
        <g pointerEvents="none">
          <circle cx={a[0]} cy={a[1]} r={r * 1.6} fill={colour} fillOpacity={0.45} stroke={colour} strokeWidth={r * 0.3} />
          {s ? (
            <>
              <line x1={a[0]} y1={a[1]} x2={s[0]} y2={s[1]} stroke={colour} strokeWidth={r * 0.28} strokeDasharray="0.8 0.6" />
              <circle cx={s[0]} cy={s[1]} r={r} fill="none" stroke={colour} strokeWidth={r * 0.3} />
            </>
          ) : null}
        </g>
      )
    }

    default:
      return null
  }
})
